import { computed, inject } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { concatMap, pipe, tap } from 'rxjs';
import {
  AsyncState,
  asyncError,
  asyncIdle,
  asyncLoading,
  asyncSuccess,
} from '../models/site-insights.model';
import { TrafficStation } from '../models/traffic-station.model';
import { TrafficStationRepository } from './traffic-station.repository';

/** The error slice's message key when the station fetch fails. */
const TRAFFIC_LOAD_ERROR_KEY = 'discover.detail.traffic.loadError';

type TrafficStationState = {
  /** The single per-layer status surface. */
  stations: AsyncState<TrafficStation[]>;
};

const initialState: TrafficStationState = {
  stations: asyncIdle(),
};

/**
 * Deliberately NOT folded into `SiteInsightsStore`: the stations are GLOBAL reference data, not
 * per-site analysis, so they stay out of the shared bundle and its transport-error banner.
 *
 * Root-provided and guarded, so the full station set is fetched at most once per session. A
 * parse or transport failure lands the `error` slice instead of throwing.
 */
export const TrafficStationStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed(({ stations }) => ({
    /** `[]` before success, so the viewport filter needs no status check. */
    stationList: computed(() => {
      const state = stations();
      return state.status === 'success' ? state.data : [];
    }),
  })),
  withMethods((store) => {
    const repo = inject(TrafficStationRepository);

    const fetch = rxMethod<void>(
      pipe(
        tap(() => patchState(store, { stations: asyncLoading() })),
        concatMap(() =>
          repo.getTrafficStations().pipe(
            tapResponse({
              next: (stations) =>
                patchState(store, { stations: asyncSuccess(stations) }),
              // Land the error slice rather than throwing: the layer degrades to a chip.
              error: () =>
                patchState(store, {
                  stations: asyncError(TRAFFIC_LOAD_ERROR_KEY),
                }),
            }),
          ),
        ),
      ),
    );

    return {
      /** A no-op while `loading` or after `success`; retries from `idle` or `error`. */
      load(): void {
        const status = store.stations().status;
        if (status === 'loading' || status === 'success') {
          return;
        }
        fetch();
      },
    };
  }),
);
